import React from 'react'

import {
  Body1,
  Body2,
  BorderStyle,
  Box,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading5,
  Link,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  RadioGroup,
} from '@gousto-internal/citrus-react'

import { client as clientRoutes } from 'config/routes'

import type { AddressOption } from '../useEditAddress'
import { useEditAddress } from '../useEditAddress'

export type EditDeliveryAddressModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirmComplete: () => void
}

const AddressOptionLabel = ({ option }: { option: AddressOption }) => (
  <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={0.5}>
    {option.name && (
      <Body1 fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
        {option.name}
      </Body1>
    )}
    {option.lines.map((line) => (
      <Body2 key={line} color={Color.ColdGrey_600}>
        {line}
      </Body2>
    ))}
  </Box>
)

export const EditDeliveryAddressModal = ({
  isOpen,
  onClose,
  onConfirmComplete,
}: EditDeliveryAddressModalProps) => {
  const { savedAddresses, selectedAddressId, setSelectedAddressId, canSave, confirm } =
    useEditAddress()

  // Unmount when closed so hook state resets each time the modal reopens.
  if (!isOpen) return null

  const handleConfirm = () => {
    confirm()
    onConfirmComplete()
  }

  const radioOptions = savedAddresses.map((option) => ({
    name: option.id,
    value: option.id,
    label: <AddressOptionLabel option={option} />,
  }))

  return (
    <Modal isOpen name="edit-delivery-address-modal" onRequestClose={onClose}>
      <ModalClose onClose={onClose} />
      <Box
        paddingH={4}
        paddingV={4}
        borderColor={Color.ColdGrey_100}
        borderBottomWidth={0.5}
        borderStyle={BorderStyle.Solid}
      >
        <Heading5 size={7}>Edit delivery address</Heading5>
      </Box>
      <ModalBody paddingV={6}>
        <Box
          maxHeight="40vh"
          style={{ overflowY: 'auto' }}
          paddingBottom={4}
          data-testid="edit-delivery-address-modal-scroll"
        >
          <RadioGroup
            outline
            value={selectedAddressId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSelectedAddressId(event.target.value)
            }
            options={radioOptions}
          />
        </Box>
        <Box paddingV={4}>
          <Link href={clientRoutes.myDetails} aria-label="Add a new delivery address">
            Add a new address
          </Link>
        </Box>
        <ModalButton
          colorVariant={ButtonColorVariant.Primary}
          width="100%"
          onClick={handleConfirm}
          disabled={!canSave}
        >
          Confirm delivery address
        </ModalButton>
      </ModalBody>
    </Modal>
  )
}
